import React from "react";
import { Target, TrendingUp, Zap, Users, Shield } from "lucide-react";
import Footer from "@/components/Footer";
import Header from "@/components/HeaderPages";

/**
 * Interface for the Feature Blocks
 */
interface FeatureProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

/**
 * Reusable Feature Block Component
 */
const FeatureBlock: React.FC<FeatureProps> = ({ icon, title, description }) => (
  <div className="p-6 bg-white rounded-xl shadow-md border border-gray-200 transition duration-300 hover:shadow-xl hover:border-gray-300">
    {/* Accent color for icons */}
    <div className="text-gray-800 mb-4">{icon}</div>
    <h3 className="text-xl font-semibold text-gray-900 mb-2">{title}</h3>
    <p className="text-gray-600 text-sm">{description}</p>
  </div>
);

/**
 * Main About Us Component (App)
 */
const AboutUs: React.FC = () => {
  return (
    <div className="min-h-screen bg-white font-sans text-gray-800">
        <Header />
      {/* Header Section - Monochrome with subtle bottom border */}
      <header className="py-16 bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-4 text-gray-900">
            Valuing the Future, Today.
          </h1>
          <p className="text-xl sm:text-2xl font-light opacity-90 max-w-3xl mx-auto text-gray-600">
            We transform the complex, multi-day process of property valuation
            into a secure, real-time data point.
          </p>
        </div>
      </header>
      {/* The Problem & Solution Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-sm font-medium text-gray-600 uppercase tracking-wider">
              Our Methodology
            </span>
            <h2 className="mt-2 text-3xl font-extrabold text-gray-900 sm:text-4xl">
              From Traditional Reports to Real-Time Clarity
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-12 items-start">
            {/* The Problem - Subtle border and shading */}
            <div className="bg-gray-50 p-8 rounded-xl shadow-inner border border-gray-200">
              <h3 className="text-2xl font-bold text-gray-800 mb-4">
                The Traditional Challenge
              </h3>
              <p className="text-gray-700 mb-4">
                As seen in conventional reports, obtaining an accurate property
                valuation involves extensive manual labor: physical inspection,
                detailed measurement, manual calculation of depreciation, and
                market research. This process is thorough, but often takes days
                or weeks, creating friction in fast-paced financial
                transactions.
              </p>
              <ul className="space-y-3 text-gray-600 list-disc list-inside">
                <li>Time-consuming site visits and reporting.</li>
                <li>Limited data pool for comparable sales.</li>
                <li>High cost per valuation.</li>
              </ul>
            </div>
            {/* Our Solution - Subtle border and shading */}
            <div className="bg-gray-50 p-8 rounded-xl shadow-inner border border-gray-200">
              <h3 className="text-2xl font-bold text-gray-800 mb-4">
                Our Real-Time Solution
              </h3>
              <p className="text-gray-700 mb-4">
                We leverage an Automated Valuation Model (AVM) that uses machine
                learning to instantly process thousands of data points—public
                records, geographical information, and transaction history—to
                provide an immediate, highly accurate estimated market value.
                This enables instant decision-making.
              </p>
              <ul className="space-y-3 text-gray-600 list-disc list-inside">
                <li>Instantaneous valuation reports (API-driven).</li>
                <li>Dynamic data sourcing and constant updates.</li>
                <li>Cost-effective and scalable.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>
      {/* The Engine: How Our AVM Works - Pure white background section */}
      <section className="py-16 bg-white border-t border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              The Instructive Process: Our AVM Engine
            </h2>
            <p className="mt-4 text-xl text-gray-600">
              We translate the core principles of a licensed valuation into
              scalable algorithms.
            </p>
          </div>
          {/* Diagram Placeholder for Instruction - Clean white box with strong border */}
          <div className="text-center my-8 p-6 bg-white border-4 border-gray-800 rounded-xl shadow-lg">
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              Automated Valuation Model (AVM) Flow
            </h3>
            <p className="text-gray-500 text-sm mb-4">
              Our system mimics the structure of a professional report, focusing
              on three key inputs.
            </p>
            {/* Instructive Diagram Tag */}
            <p className="text-lg font-mono p-4 bg-gray-100 rounded-lg border-dashed border-gray-400 border-2 text-gray-700"></p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mt-12">
            {/* Feature Blocks now use the monochrome style defined in FeatureBlock component */}
            <FeatureBlock
              icon={<Target size={32} strokeWidth={1.5} />}
              title="Comparable Sales Analysis (Market Value)"
              description="We use real-time transaction data and public sales records to find the closest comparable properties, similar to the Market Price method used by valuers."
            />
            <FeatureBlock
              icon={<TrendingUp size={32} strokeWidth={1.5} />}
              title="Cost & Depreciation (Asset Value)"
              description="Algorithms calculate current replacement costs and apply dynamic depreciation schedules based on property age and condition data, mirroring the detailed calculations in licensed reports."
            />
            <FeatureBlock
              icon={<Zap size={32} strokeWidth={1.5} />}
              title="Land Value Integration"
              description="By isolating and analyzing regional plot market prices and factoring in cadastral data (area, UPI/plot number), we accurately determine the underlying land value."
            />
          </div>
        </div>
      </section>
      {/* Our Commitment Section */}
      <section className="py-16 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl mb-4">
                Built on Trust and Transparency
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                While our AVM delivers instant results, we understand that human
                expertise is irreplaceable for final sign-off. Our platform is
                designed to complement, not replace, the licensed valuer,
                providing them with the fastest, most comprehensive data set
                available.
              </p>
              <div className="space-y-4">
                <div className="flex items-start">
                  {/* Accent color for key points */}
                  <Shield className="w-6 h-6 text-gray-800 flex-shrink-0 mt-1 mr-3" />
                  <p className="text-gray-700">
                    <strong className="font-semibold">Compliance First:</strong>{" "}
                    Our models are regularly audited to ensure they meet
                    international valuation standards.
                  </p>
                </div>
                <div className="flex items-start">
                  <Users className="w-6 h-6 text-gray-800 flex-shrink-0 mt-1 mr-3" />
                  <p className="text-gray-700">
                    <strong className="font-semibold">Hybrid Approach:</strong>{" "}
                    Instant AVM for speed; licensed valuer integration for final
                    accuracy and certification.
                  </p>
                </div>
              </div>
            </div>
            {/* Team Image Placeholder - Placeholder uses a gray background */}
            <div className="relative h-64 md:h-96 rounded-xl overflow-hidden shadow-xl border border-gray-300">
              <img
                src={`https://placehold.co/800x600/D1D5DB/1F2937?text=The+Team+Behind+The+Data`}
                alt="Our dedicated team working on data models"
                className="w-full h-full object-cover"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.onerror = null;
                  target.src =
                    "https://placehold.co/800x600/D1D5DB/1F2937?text=Team+Photo";
                }}
              />
            </div>
          </div>
        </div>
      </section>
      {/* Footer - Dark gray for high contrast */}
      <Footer />
    </div>
  );
};

export default AboutUs;
